/**
 * Runtime configuration that's loaded from public/config.json
 * This is the SINGLE SOURCE OF TRUTH for runtime configuration.
 * Values here can be changed without rebuilding the app.
 */

export type RuntimeMode = 'dev' | 'prod';

export interface RuntimeConfig {
  mode: RuntimeMode;
  env: string;
  // Cognito
  VITE_AWS_COGNITO_USER_POOL_ID?: string;
  VITE_AWS_COGNITO_CLIENT_ID?: string;
  VITE_AWS_COGNITO_USER_POOL_AUTH_TOKEN?: string;
  VITE_AWS_COGNITO_REDIRECT_URI?: string;
  VITE_AWS_COGNITO_LOGOUT_URI?: string;
  VITE_AWS_COGNITO_OAUTH_RESPONSE_TYPE?: string;
  // AWS
  VITE_AWS_SOCKET?: string;
  VITE_DOMAIN?: string;
  // APIs
  VITE_STRIPE_API_URL?: string;
  VITE_C1_API_URL?: string;
  VITE_REGISTRATION_API_URL?: string;
  VITE_ONBOARDING_API_URL?: string;
  VITE_KYB_API_URL?: string;
  // Session
  VITE_IDLE_TIMEOUT?: string;
  VITE_WARNING_DURATION?: string;
  // Socket
  VITE_SOCKET_RECONNECTION_DELAY?: string;
  VITE_SOCKET_RECONNECTION_DELAY_MAX?: string;
  VITE_SOCKET_RECONNECTION_ATTEMPTS?: string;
  VITE_SOCKET_RANDOMIZATION_FACTOR?: string;
}

let cachedConfig: RuntimeConfig | null = null;

/**
 * Fetch runtime config from public/config.json
 */
export async function loadRuntimeConfig(): Promise<RuntimeConfig> {
  if (cachedConfig) {
    return cachedConfig;
  }

  try {
    const response = await fetch('/config.json');
    const config = await response.json();
    cachedConfig = config;
    return config;
  } catch (error) {
    cachedConfig = { mode: 'dev', env: 'development' };
    return cachedConfig;
  }
}

/**
 * Get cached runtime config (synchronous)
 */
export function getRuntimeConfig(): RuntimeConfig | null {
  return cachedConfig;
}

/**
 * Derive the signup URL from the runtime env field.
 * e.g. env="dev1" → "https://signup-dev1.arcamltest.com.au"
 *      env="live" → "https://signup.arcaml.com.au"
 */
export function getSignupUrl(): string {
  const env = cachedConfig?.env;
  if (!env) {
    throw new Error('Environment not configured. Set env in config.json.');
  }
  if (env === 'live' || env === 'prod' || env === 'production') {
    return 'https://signup.arcaml.com.au';
  }
  if (['dev1', 'dev2', 'test', 'demo'].includes(env)) {
    return `https://signup-${env}.arcamltest.com.au`;
  }
  throw new Error(`Unknown environment: ${env}. Expected one of: live, prod, production, dev1, dev2, test, demo.`);
}

